<script lang="ts">
  import PageSection from '@athena-lib/Page/PageSection.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { HeadingSmall } from '@athena-lib/Text';
  import { Column, Columns } from '@athena-lib/Columns';
  import { Button } from '@athena-lib/Button';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import HeadingXSmall from '@athena-lib/Text/HeadingXSmall/HeadingXSmall.svelte';
  import { Checkbox } from '@athena-lib/Checkbox';
  import { TextField } from '@athena-lib/Fields';
  import Modal from '@athena-lib/Modal/Modal.svelte';
  import { Type } from '@sinclair/typebox';
  import Avatar from './Avatar.svelte';
  import AccountTabs from './AccountTabs.svelte';
  import CountryStateSelector from './CountryStateSelector.svelte';
  import EditPasswordForm from './EditPasswordForm.svelte';
  import { isEU } from './utils';
  import type { EditAccountData } from '$lib/server/external/types';
  import { goto } from '$app/navigation';
  import { post } from '$lib/http';

  const translations = getCommonTranslations();

  const { data }: { data: EditAccountData } = $props();
  let formData: EditAccountData['accountData']['billing'] = $state(data.accountData.billing);

  function save() {
    post('/account/set_user_data', { address: formData }, Type.Object({ user: Type.String() }));
    goto('/customer');
  }

  $effect(() => {
    const countryCode = formData.country_code;
    formData.is_eu = countryCode ? isEU(countryCode) : null;
  });
  let changePasswordModalIsOpen = $state(false);
</script>

{#if changePasswordModalIsOpen}
  <Modal
    class="p-8 pt-4"
    showModal={changePasswordModalIsOpen}
    onClose={() => {
      changePasswordModalIsOpen = false;
    }}
    closable={true}
    size="sm"
  >
    <Stack>
      <StackItem>
        <HeadingSmall>
          {translations.password}
        </HeadingSmall>
      </StackItem>
      <StackItem>
        <EditPasswordForm />
      </StackItem>
    </Stack>
  </Modal>
{/if}
<AccountTabs currentPage="customer" market={data.market} accountData={data.accountData}
></AccountTabs>
<PageSection class="grow" backgroundColor="medium">
  <Stack>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <HeadingSmall>{translations['accountProfilePageTitle']}</HeadingSmall>
        </Column>
        <Column class="hidden flex-row justify-end gap-4 md:flex">
          <Button onClick={save} size="small">{translations.save}</Button>
          <Button element="a" link={{ href: '/customer' }} color="white" size="small"
            >{translations.cancel}</Button
          >
        </Column>
      </Columns>
    </StackItem>
    <StackItem>
      <hr class="mb-4 mt-3" />
    </StackItem>
    <StackItem class="flex justify-center">
      <Avatar src={data.accountData.avatarUrl} size="big" />
    </StackItem>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <HeadingXSmall>{translations.billingAddress}</HeadingXSmall>
        </Column>
        <Column class="flex flex-row justify-end ">
          <Checkbox
            id="business-checkbox"
            bind:checked={formData.is_company}
            label={translations.business}
          />
        </Column>
      </Columns>
    </StackItem>
    {#if formData.is_company}
      <StackItem>
        <Columns collapseBelow="md">
          <Column>
            <TextField bind:value={formData.name} type="text" label={translations.companyName} />
          </Column>
          <Column>
            {#if formData.country_code === 'se' || formData.country_code === 'no'}
              <TextField
                bind:value={formData.company_org_number}
                type="text"
                label={translations.companyOrgNumber}
              />
            {:else if formData.is_eu}
              <TextField
                bind:value={formData.company_vat_number}
                type="text"
                label={translations.companyVatNumber}
              />
            {/if}
          </Column>
        </Columns>
      </StackItem>
    {/if}
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <TextField bind:value={formData.first_name} type="text" label={translations.firstName} />
        </Column>
        <Column>
          <TextField bind:value={formData.surname} type="text" label={translations.lastName} />
        </Column>
      </Columns>
    </StackItem>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <TextField bind:value={formData.address} type="text" label={translations.address1} />
        </Column>
        <Column>
          <TextField bind:value={formData.address2} type="text" label={translations.address2} />
        </Column>
      </Columns>
    </StackItem>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <Columns collapseBelow="md">
            <Column>
              <TextField bind:value={formData.zip} type="text" label={translations.zipCode} />
            </Column>
            <Column>
              <TextField bind:value={formData.city} type="text" label={translations.city} />
            </Column>
          </Columns>
        </Column>
        <Column>
          <CountryStateSelector
            countries={data.countries}
            bind:county={formData.county}
            bind:country_code={formData.country_code}
          />
        </Column>
      </Columns>
    </StackItem>
    <StackItem>
      <Columns collapseBelow="md">
        <Column>
          <TextField bind:value={formData.phone} type="text" label={translations.phone} />
        </Column>
        <Column>
          <TextField
            value={data.accountData.userInfo.email}
            disabled
            type="text"
            label={translations.email}
          />
        </Column>
      </Columns>
    </StackItem>
    <StackItem class="flex flex-row justify-end">
      <Button
        size="small"
        onClick={() => {
          changePasswordModalIsOpen = true;
        }}>{translations.changePassword}</Button
      >
    </StackItem>
    <StackItem class="hidden justify-center gap-3  max-md:flex">
      <Button wide element="a" link={{ href: '/customer' }} color="white" size="small"
        >{translations.cancel}</Button
      >
      <Button wide onClick={save} size="small">{translations.save}</Button>
    </StackItem>
  </Stack>
</PageSection>
